import React, { FC, useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Card,
  Steps,
  Form,
  Input,
  Button,
  Typography,
  message,
  Spin,
  Result,
  Icon,
  Row,
  Col,
  Divider
} from 'antd';
import { FormComponentProps } from 'antd/lib/form';

const { Title, Paragraph, Text } = Typography;

interface UserStatusResponse {
  id?: string;
  name?: string;
  email: string;
  cpf?: string;
  profissao?: string;
  cargo?: string;
  empresa?: string;
  endereco?: string;
  ddd_celular?: string;
  telefone_celular?: string;
  curriculo_lattes?: string;
  curriculo_linkedin?: string;
  has_account: boolean;
}

const VerificationPageBase: FC<FormComponentProps> = ({ form }) => {
  const location = useLocation();
  const history = useHistory();
  const { getFieldDecorator, setFieldsValue, getFieldValue } = form;

  const queryParams = new URLSearchParams(location.search);
  const tokenParam = queryParams.get('token') || '';

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [userData, setUserData] = useState<UserStatusResponse | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    const validateEmailParam = async () => {
      try {
        if (!tokenParam) {
          setEmailError('Link de verificação ausente ou malformado.');
          setLoading(false);
          return;
        }

        const params = new URLSearchParams();
        params.append('token', tokenParam);

        const response = await fetch(
          `https://api2.forumconhecimento.com.br/plat-auth-login/verification/check-status?${params.toString()}`
        );

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(
            errBody.error || 'Não foi possível verificar a conta.'
          );
        }

        const jsonResponse = await response.json();
        const data: UserStatusResponse =
          jsonResponse && jsonResponse.data ? jsonResponse.data : jsonResponse;

        const resolvedUser: UserStatusResponse = {
          id: data.id || '',
          name: data.name || '',
          email: data.email || '',
          cpf: data.cpf || '',
          profissao: data.profissao || '',
          cargo: data.cargo || '',
          empresa: data.empresa || '',
          endereco: data.endereco || '',
          ddd_celular: data.ddd_celular || '',
          telefone_celular: data.telefone_celular || '',
          curriculo_lattes: data.curriculo_lattes || '',
          curriculo_linkedin: data.curriculo_linkedin || '',
          has_account: Boolean(data && data.has_account)
        };

        setUserData(resolvedUser);
        setCurrentStep(1);

        setTimeout(() => {
          setFieldsValue({
            cpf: resolvedUser.cpf,
            profissao: resolvedUser.profissao,
            cargo: resolvedUser.cargo,
            empresa: resolvedUser.empresa,
            endereco: resolvedUser.endereco,
            ddd_celular: resolvedUser.ddd_celular,
            telefone_celular: resolvedUser.telefone_celular,
            curriculo_lattes: resolvedUser.curriculo_lattes,
            curriculo_linkedin: resolvedUser.curriculo_linkedin
          });
        }, 50);
      } catch (err: any) {
        setEmailError(err.message || 'Erro ao consultar os dados do autor.');
      } finally {
        setLoading(false);
      }
    };

    validateEmailParam();
  }, [tokenParam, setFieldsValue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    form.validateFields(async (err, values) => {
      if (err) return;

      setSaving(true);
      try {
        const userEmail = (userData && userData.email) || '';
        const emailClean = userEmail.toLowerCase().replace(/[^a-z0-9]/g, '-');
        const resolvedAuthorId =
          (userData && userData.id) || `author_${emailClean}`;
        const userName = (userData && userData.name) || '';

        const response = await fetch(
          'https://api2.forumconhecimento.com.br/plat-auth-login/verification/update-profile',
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              author_id: resolvedAuthorId,
              email: userEmail,
              name: userName,
              cpf: values.cpf,
              profissao: values.profissao,
              cargo: values.cargo,
              empresa: values.empresa,
              endereco: values.endereco,
              ddd_celular: values.ddd_celular,
              telefone_celular: values.telefone_celular,
              curriculo_lattes: values.curriculo_lattes,
              curriculo_linkedin: values.curriculo_linkedin
            })
          }
        );

        if (!response.ok) {
          throw new Error('Erro ao salvar dados.');
        }

        message.success('Cadastro concluído com sucesso!');
        setCurrentStep(2);
      } catch (error: any) {
        message.error(error.message || 'Erro ao atualizar informações.');
      } finally {
        setSaving(false);
      }
    });
  };

  const handleProceed = () => {
    const safeEmail = encodeURIComponent((userData && userData.email) || '');
    window.location.href = `https://forumconhecimento.com.br/email=${safeEmail}`;
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f2f2f2'
        }}>
        <Spin size="large" tip="Validando link de acesso..." />
      </div>
    );
  }

  if (emailError) {
    return (
      <div
        style={{
          backgroundColor: '#f2f2f2',
          minHeight: '100vh',
          padding: '60px 16px'
        }}>
        <Card
          style={{
            maxWidth: 520,
            margin: '0 auto',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}>
          <Result
            status="error"
            title="Link Inválido"
            subTitle={emailError}
            extra={[
              <Button
                type="primary"
                key="login"
                style={{ backgroundColor: '#0038a8', borderColor: '#0038a8' }}
                onClick={() => history.push('/login')}>
                Ir para o Login
              </Button>
            ]}
          />
        </Card>
      </div>
    );
  }

  const authorName = (userData && userData.name) || 'Autor Registrado';
  const authorEmail = (userData && userData.email) || '';
  const hasUserAccount = userData && userData.has_account;

  const safeCpf = (userData && userData.cpf) || '';
  const safeProfissao = (userData && userData.profissao) || '';
  const safeCargo = (userData && userData.cargo) || '';
  const safeEmpresa = (userData && userData.empresa) || '';
  const safeEndereco = (userData && userData.endereco) || '';
  const safeDddCelular = (userData && userData.ddd_celular) || '';
  const safeTelefoneCelular = (userData && userData.telefone_celular) || '';
  const safeCurriculoLattes = (userData && userData.curriculo_lattes) || '';
  const safeCurriculoLinkedin = (userData && userData.curriculo_linkedin) || '';

  // 1. Verifica se o CPF veio preenchido no carregamento inicial da API
  const hasInitialCpf = Boolean(
    userData && userData.cpf && userData.cpf.trim() !== ''
  );

  // 2. Obtém o valor atual do CPF do formulário
  const currentCpfValue = getFieldValue('cpf') || '';
  const cleanCpfDigits = currentCpfValue.replace(/\D/g, '');

  // 3. Validação: O CPF precisa ter 11 dígitos para habilitar o botão
  const isCpfValid = cleanCpfDigits.length === 11;

  return (
    <div
      style={{
        backgroundColor: '#f2f2f2',
        minHeight: '100vh',
        padding: '40px 16px'
      }}>
      <div style={{ maxWidth: 650, margin: '0 auto' }}>
        <Card
          style={{
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            border: '1px solid #e8e8e8'
          }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <img
              src="/assets/images/logo-email-new.png"
              alt="Fórum Conhecimento Jurídico"
              style={{
                maxHeight: 60,
                maxWidth: '100%',
                objectFit: 'contain'
              }}
            />
          </div>

          <Steps current={currentStep} style={{ marginBottom: 32 }}>
            <Steps.Step title="Validação" />
            <Steps.Step title="Dados Cadastrais" />
            <Steps.Step title="Conclusão" />
          </Steps>

          {currentStep === 1 && (
            <Form layout="vertical" onSubmit={handleSubmit}>
              <div style={{ textAlign: 'center', marginBottom: 28 }}>
                <Title
                  level={4}
                  style={{ color: '#111', marginBottom: 4, fontWeight: 600 }}>
                  Prezado(a) Autor(a)
                </Title>
                <Paragraph
                  type="secondary"
                  style={{ fontSize: 14, marginBottom: 12 }}>
                  Confirme e complete suas informações para concluir o acesso à
                  plataforma.
                </Paragraph>

                <div
                  style={{
                    background: '#f8f9fa',
                    padding: '12px 16px',
                    borderRadius: 6,
                    display: 'inline-block',
                    textAlign: 'left',
                    minWidth: '80%'
                  }}>
                  <Text
                    strong
                    style={{
                      fontSize: 15,
                      color: '#0f265c',
                      display: 'block'
                    }}>
                    {authorName}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    <Icon type="mail" style={{ marginRight: 6 }} />
                    {authorEmail}
                  </Text>
                </div>
              </div>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={
                      <Text style={{ fontWeight: 500 }}>
                        CPF <span style={{ color: 'red' }}>*</span>
                      </Text>
                    }>
                    {getFieldDecorator('cpf', {
                      initialValue: safeCpf,
                      rules: [
                        { required: true, message: 'Informe o CPF.' },
                        {
                          validator: (_rule, value, callback) => {
                            const digits = (value || '').replace(/\D/g, '');
                            if (digits && digits.length !== 11) {
                              callback('O CPF deve ter 11 dígitos.');
                            } else {
                              callback();
                            }
                          }
                        }
                      ]
                    })(
                      <Input
                        placeholder="000.000.000-00"
                        size="large"
                        disabled={hasInitialCpf}
                      />
                    )}
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label={<Text style={{ fontWeight: 500 }}>Profissão</Text>}>
                    {getFieldDecorator('profissao', {
                      initialValue: safeProfissao
                    })(
                      <Input
                        size="large"
                        placeholder="Ex: Advogado, Professor"
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label={<Text style={{ fontWeight: 500 }}>Cargo</Text>}>
                    {getFieldDecorator('cargo', { initialValue: safeCargo })(
                      <Input size="large" placeholder="Seu cargo atual" />
                    )}
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label={
                      <Text style={{ fontWeight: 500 }}>
                        Empresa / Instituição
                      </Text>
                    }>
                    {getFieldDecorator('empresa', {
                      initialValue: safeEmpresa
                    })(<Input size="large" placeholder="Nome da empresa" />)}
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label={
                  <Text style={{ fontWeight: 500 }}>
                    Endereço Residencial/Profissional
                  </Text>
                }>
                {getFieldDecorator('endereco', { initialValue: safeEndereco })(
                  <Input
                    size="large"
                    placeholder="Rua, número, complemento - Cidade/UF"
                  />
                )}
              </Form.Item>

              <Form.Item
                label={
                  <Text style={{ fontWeight: 500 }}>Telefone Celular</Text>
                }>
                <Row gutter={8}>
                  <Col span={6}>
                    {getFieldDecorator('ddd_celular', {
                      initialValue: safeDddCelular
                    })(<Input placeholder="DDD" maxLength={3} size="large" />)}
                  </Col>
                  <Col span={18}>
                    {getFieldDecorator('telefone_celular', {
                      initialValue: safeTelefoneCelular
                    })(
                      <Input
                        placeholder="Número sem traços"
                        maxLength={10}
                        size="large"
                      />
                    )}
                  </Col>
                </Row>
              </Form.Item>

              <Divider
                orientation="left"
                style={{ marginTop: 24, marginBottom: 20 }}>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 13,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                  Informações Acadêmicas
                </Text>
              </Divider>

              <Form.Item
                label={
                  <Text style={{ fontWeight: 500 }}>
                    Link do Currículo Lattes
                  </Text>
                }>
                {getFieldDecorator('curriculo_lattes', {
                  initialValue: safeCurriculoLattes
                })(
                  <Input size="large" placeholder="http://lattes.cnpq.br/..." />
                )}
              </Form.Item>

              <Form.Item
                label={
                  <Text style={{ fontWeight: 500 }}>Link do LinkedIn</Text>
                }>
                {getFieldDecorator('curriculo_linkedin', {
                  initialValue: safeCurriculoLinkedin
                })(
                  <Input
                    size="large"
                    placeholder="https://linkedin.com/in/..."
                  />
                )}
              </Form.Item>

              <Form.Item style={{ marginTop: 32, marginBottom: 8 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  block
                  loading={saving}
                  disabled={!isCpfValid || saving}
                  style={{
                    backgroundColor: '#0038a8',
                    borderColor: '#0038a8',
                    height: 46,
                    fontWeight: 600,
                    fontSize: 16,
                    borderRadius: 4
                  }}>
                  Salvar e Continuar
                </Button>
              </Form.Item>
            </Form>
          )}

          {currentStep === 2 && (
            <Result
              status="success"
              icon={<React.Fragment />}
              title="Seus dados foram atualizados!"
              subTitle={
                hasUserAccount
                  ? 'Clique no botão abaixo para fazer login na plataforma ou efetuar o cadastro caso não tenha usuário.'
                  : 'Seus dados foram salvos. Clique abaixo para cadastrar sua senha e ativar o acesso.'
              }
              extra={[
                <Button
                  type="primary"
                  size="large"
                  key="proceed"
                  block
                  style={{
                    backgroundColor: '#0038a8',
                    borderColor: '#0038a8',
                    height: 46,
                    fontWeight: 600,
                    borderRadius: 4
                  }}
                  onClick={handleProceed}>
                  {hasUserAccount
                    ? 'Ir para o Login'
                    : 'Cadastrar Senha de Acesso'}
                </Button>
              ]}
            />
          )}
        </Card>
      </div>
    </div>
  );
};

export const VerificationPage =
  Form.create<FormComponentProps>()(VerificationPageBase);
// import React, { FC, useState, useEffect } from 'react';
// import { useLocation, useHistory } from 'react-router-dom';
// import {
//   Card,
//   Steps,
//   Form,
//   Input,
//   Button,
//   Typography,
//   message,
//   Spin,
//   Result,
//   Icon,
//   Row,
//   Col,
//   Divider
// } from 'antd';
// import { FormComponentProps } from 'antd/lib/form';

// const { Title, Paragraph, Text } = Typography;

// interface UserStatusResponse {
//   id?: string;
//   name?: string;
//   email: string;
//   cpf?: string;
//   profissao?: string;
//   cargo?: string;
//   empresa?: string;
//   endereco?: string;
//   ddd_celular?: string;
//   telefone_celular?: string;
//   curriculo_lattes?: string;
//   curriculo_linkedin?: string;
//   has_account: boolean;
// }

// const VerificationPageBase: FC<FormComponentProps> = ({ form }) => {
//   const location = useLocation();
//   const history = useHistory();
//   const { getFieldDecorator, setFieldsValue } = form;

//   const queryParams = new URLSearchParams(location.search);
//   const tokenParam = queryParams.get('token') || '';

//   const [currentStep, setCurrentStep] = useState<number>(0);
//   const [loading, setLoading] = useState<boolean>(true);
//   const [saving, setSaving] = useState<boolean>(false);
//   const [userData, setUserData] = useState<UserStatusResponse | null>(null);
//   const [emailError, setEmailError] = useState<string | null>(null);

//   useEffect(() => {
//     const validateEmailParam = async () => {
//       try {
//         if (!tokenParam) {
//           setEmailError('Link de verificação ausente ou malformado.');
//           setLoading(false);
//           return;
//         }

//         const params = new URLSearchParams();
//         params.append('token', tokenParam);

//         const response = await fetch(
//           `https://api2.forumconhecimento.com.br/plat-auth-login/verification/check-status?${params.toString()}`
//         );

//         if (!response.ok) {
//           const errBody = await response.json().catch(() => ({}));
//           throw new Error(
//             errBody.error || 'Não foi possível verificar a conta.'
//           );
//         }

//         const jsonResponse = await response.json();
//         const data: UserStatusResponse =
//           jsonResponse && jsonResponse.data ? jsonResponse.data : jsonResponse;

//         const resolvedUser: UserStatusResponse = {
//           id: data.id || '',
//           name: data.name || '',
//           email: data.email || '',
//           cpf: data.cpf || '',
//           profissao: data.profissao || '',
//           cargo: data.cargo || '',
//           empresa: data.empresa || '',
//           endereco: data.endereco || '',
//           ddd_celular: data.ddd_celular || '',
//           telefone_celular: data.telefone_celular || '',
//           curriculo_lattes: data.curriculo_lattes || '',
//           curriculo_linkedin: data.curriculo_linkedin || '',
//           has_account: Boolean(data && data.has_account)
//         };

//         setUserData(resolvedUser);
//         setCurrentStep(1);

//         setTimeout(() => {
//           setFieldsValue({
//             cpf: resolvedUser.cpf,
//             profissao: resolvedUser.profissao,
//             cargo: resolvedUser.cargo,
//             empresa: resolvedUser.empresa,
//             endereco: resolvedUser.endereco,
//             ddd_celular: resolvedUser.ddd_celular,
//             telefone_celular: resolvedUser.telefone_celular,
//             curriculo_lattes: resolvedUser.curriculo_lattes,
//             curriculo_linkedin: resolvedUser.curriculo_linkedin
//           });
//         }, 50);
//       } catch (err: any) {
//         setEmailError(err.message || 'Erro ao consultar os dados do autor.');
//       } finally {
//         setLoading(false);
//       }
//     };

//     validateEmailParam();
//   }, [tokenParam, setFieldsValue]);

//   const handleSubmit = (e: React.FormEvent) => {
//     e.preventDefault();

//     form.validateFields(async (err, values) => {
//       if (err) return;

//       setSaving(true);
//       try {
//         const userEmail = (userData && userData.email) || '';
//         const emailClean = userEmail.toLowerCase().replace(/[^a-z0-9]/g, '-');
//         const resolvedAuthorId =
//           (userData && userData.id) || `author_${emailClean}`;
//         const userName = (userData && userData.name) || '';

//         const response = await fetch(
//           'https://api2.forumconhecimento.com.br/plat-auth-login/verification/update-profile',
//           {
//             method: 'PUT',
//             headers: { 'Content-Type': 'application/json' },
//             body: JSON.stringify({
//               author_id: resolvedAuthorId,
//               email: userEmail,
//               name: userName,
//               cpf: values.cpf,
//               profissao: values.profissao,
//               cargo: values.cargo,
//               empresa: values.empresa,
//               endereco: values.endereco,
//               ddd_celular: values.ddd_celular,
//               telefone_celular: values.telefone_celular,
//               curriculo_lattes: values.curriculo_lattes,
//               curriculo_linkedin: values.curriculo_linkedin
//             })
//           }
//         );

//         if (!response.ok) {
//           throw new Error('Erro ao salvar dados.');
//         }

//         message.success('Cadastro concluído com sucesso!');
//         setCurrentStep(2);
//       } catch (error: any) {
//         message.error(error.message || 'Erro ao atualizar informações.');
//       } finally {
//         setSaving(false);
//       }
//     });
//   };

//   const handleProceed = () => {
//     const hasAccount = userData && userData.has_account;
//     const safeEmail = encodeURIComponent((userData && userData.email) || '');

//     if (hasAccount) {
//       window.location.href = `https://forumconhecimento.com.br/email=${safeEmail}`;
//     } else {
//       window.location.href = `https://forumconhecimento.com.br/email=${safeEmail}`;
//     }
//   };

//   if (loading) {
//     return (
//       <div
//         style={{
//           display: 'flex',
//           justifyContent: 'center',
//           alignItems: 'center',
//           minHeight: '100vh',
//           backgroundColor: '#f2f2f2'
//         }}>
//         <Spin size="large" tip="Validando link de acesso..." />
//       </div>
//     );
//   }

//   if (emailError) {
//     return (
//       <div
//         style={{
//           backgroundColor: '#f2f2f2',
//           minHeight: '100vh',
//           padding: '60px 16px'
//         }}>
//         <Card
//           style={{
//             maxWidth: 520,
//             margin: '0 auto',
//             borderRadius: 8,
//             boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
//           }}>
//           <Result
//             status="error"
//             title="Link Inválido"
//             subTitle={emailError}
//             extra={[
//               <Button
//                 type="primary"
//                 key="login"
//                 style={{ backgroundColor: '#0038a8', borderColor: '#0038a8' }}
//                 onClick={() => history.push('/login')}>
//                 Ir para o Login
//               </Button>
//             ]}
//           />
//         </Card>
//       </div>
//     );
//   }

//   const authorName = (userData && userData.name) || 'Autor Registrado';
//   const authorEmail = (userData && userData.email) || '';
//   const hasUserAccount = userData && userData.has_account;

//   const safeCpf = (userData && userData.cpf) || '';
//   const safeProfissao = (userData && userData.profissao) || '';
//   const safeCargo = (userData && userData.cargo) || '';
//   const safeEmpresa = (userData && userData.empresa) || '';
//   const safeEndereco = (userData && userData.endereco) || '';
//   const safeDddCelular = (userData && userData.ddd_celular) || '';
//   const safeTelefoneCelular = (userData && userData.telefone_celular) || '';
//   const safeCurriculoLattes = (userData && userData.curriculo_lattes) || '';
//   const safeCurriculoLinkedin = (userData && userData.curriculo_linkedin) || '';

//   return (
//     <div
//       style={{
//         backgroundColor: '#f2f2f2',
//         minHeight: '100vh',
//         padding: '40px 16px'
//       }}>
//       <div style={{ maxWidth: 650, margin: '0 auto' }}>
//         <Card
//           style={{
//             borderRadius: 8,
//             boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
//             border: '1px solid #e8e8e8'
//           }}>
//           {/* Logo da Empresa */}
//           <div style={{ textAlign: 'center', marginBottom: 24 }}>
//             <img
//               src="/assets/images/logo-email-new.png"
//               alt="Fórum Conhecimento Jurídico"
//               style={{
//                 maxHeight: 60,
//                 maxWidth: '100%',
//                 objectFit: 'contain'
//               }}
//             />
//           </div>

//           <Steps current={currentStep} style={{ marginBottom: 32 }}>
//             <Steps.Step title="Validação" />
//             <Steps.Step title="Dados Cadastrais" />
//             <Steps.Step title="Conclusão" />
//           </Steps>

//           {currentStep === 1 && (
//             <Form layout="vertical" onSubmit={handleSubmit}>
//               <div style={{ textAlign: 'center', marginBottom: 28 }}>
//                 <Title
//                   level={4}
//                   style={{ color: '#111', marginBottom: 4, fontWeight: 600 }}>
//                   Prezado(a) Autor(a)
//                 </Title>
//                 <Paragraph
//                   type="secondary"
//                   style={{ fontSize: 14, marginBottom: 12 }}>
//                   Confirme e complete suas informações para concluir o acesso à
//                   plataforma.
//                 </Paragraph>

//                 <div
//                   style={{
//                     background: '#f8f9fa',
//                     padding: '12px 16px',
//                     borderRadius: 6,
//                     display: 'inline-block',
//                     textAlign: 'left',
//                     minWidth: '80%'
//                   }}>
//                   <Text
//                     strong
//                     style={{
//                       fontSize: 15,
//                       color: '#0f265c',
//                       display: 'block'
//                     }}>
//                     {authorName}
//                   </Text>
//                   <Text type="secondary" style={{ fontSize: 13 }}>
//                     <Icon type="mail" style={{ marginRight: 6 }} />
//                     {authorEmail}
//                   </Text>
//                 </div>
//               </div>

//               {/* Grid 2 colunas para CPF e Profissão */}
//               <Row gutter={16}>
//                 <Col span={12}>
//                   <Form.Item
//                     label={
//                       <Text style={{ fontWeight: 500 }}>
//                         CPF <span style={{ color: 'red' }}>*</span>
//                       </Text>
//                     }>
//                     {getFieldDecorator('cpf', {
//                       initialValue: safeCpf,
//                       rules: [{ required: true, message: 'Informe o CPF.' }]
//                     })(<Input placeholder="000.000.000-00" size="large" />)}
//                   </Form.Item>
//                 </Col>

//                 <Col span={12}>
//                   <Form.Item
//                     label={<Text style={{ fontWeight: 500 }}>Profissão</Text>}>
//                     {getFieldDecorator('profissao', {
//                       initialValue: safeProfissao
//                     })(
//                       <Input
//                         size="large"
//                         placeholder="Ex: Advogado, Professor"
//                       />
//                     )}
//                   </Form.Item>
//                 </Col>
//               </Row>

//               <Row gutter={16}>
//                 <Col span={12}>
//                   <Form.Item
//                     label={<Text style={{ fontWeight: 500 }}>Cargo</Text>}>
//                     {getFieldDecorator('cargo', { initialValue: safeCargo })(
//                       <Input size="large" placeholder="Seu cargo atual" />
//                     )}
//                   </Form.Item>
//                 </Col>

//                 <Col span={12}>
//                   <Form.Item
//                     label={
//                       <Text style={{ fontWeight: 500 }}>
//                         Empresa / Instituição
//                       </Text>
//                     }>
//                     {getFieldDecorator('empresa', {
//                       initialValue: safeEmpresa
//                     })(<Input size="large" placeholder="Nome da empresa" />)}
//                   </Form.Item>
//                 </Col>
//               </Row>

//               <Form.Item
//                 label={
//                   <Text style={{ fontWeight: 500 }}>
//                     Endereço Residencial/Profissional
//                   </Text>
//                 }>
//                 {getFieldDecorator('endereco', { initialValue: safeEndereco })(
//                   <Input
//                     size="large"
//                     placeholder="Rua, número, complemento - Cidade/UF"
//                   />
//                 )}
//               </Form.Item>

//               <Form.Item
//                 label={
//                   <Text style={{ fontWeight: 500 }}>Telefone Celular</Text>
//                 }>
//                 <Row gutter={8}>
//                   <Col span={6}>
//                     {getFieldDecorator('ddd_celular', {
//                       initialValue: safeDddCelular
//                     })(<Input placeholder="DDD" maxLength={3} size="large" />)}
//                   </Col>
//                   <Col span={18}>
//                     {getFieldDecorator('telefone_celular', {
//                       initialValue: safeTelefoneCelular
//                     })(
//                       <Input
//                         placeholder="Número sem traços"
//                         maxLength={10}
//                         size="large"
//                       />
//                     )}
//                   </Col>
//                 </Row>
//               </Form.Item>

//               <Divider
//                 orientation="left"
//                 style={{ marginTop: 24, marginBottom: 20 }}>
//                 <Text
//                   type="secondary"
//                   style={{
//                     fontSize: 13,
//                     textTransform: 'uppercase',
//                     letterSpacing: '0.5px'
//                   }}>
//                   Informações Acadêmicas
//                 </Text>
//               </Divider>

//               <Form.Item
//                 label={
//                   <Text style={{ fontWeight: 500 }}>
//                     Link do Currículo Lattes
//                   </Text>
//                 }>
//                 {getFieldDecorator('curriculo_lattes', {
//                   initialValue: safeCurriculoLattes
//                 })(
//                   <Input size="large" placeholder="http://lattes.cnpq.br/..." />
//                 )}
//               </Form.Item>

//               <Form.Item
//                 label={
//                   <Text style={{ fontWeight: 500 }}>Link do LinkedIn</Text>
//                 }>
//                 {getFieldDecorator('curriculo_linkedin', {
//                   initialValue: safeCurriculoLinkedin
//                 })(
//                   <Input
//                     size="large"
//                     placeholder="https://linkedin.com/in/..."
//                   />
//                 )}
//               </Form.Item>

//               <Form.Item style={{ marginTop: 32, marginBottom: 8 }}>
//                 <Button
//                   type="primary"
//                   htmlType="submit"
//                   size="large"
//                   block
//                   loading={saving}
//                   style={{
//                     backgroundColor: '#0038a8',
//                     borderColor: '#0038a8',
//                     height: 46,
//                     fontWeight: 600,
//                     fontSize: 16,
//                     borderRadius: 4
//                   }}>
//                   Salvar e Continuar
//                 </Button>
//               </Form.Item>
//             </Form>
//           )}

//           {currentStep === 2 && (
//             <Result
//               status="success"
//               icon={<React.Fragment />}
//               title="Seus dados foram atualizados!"
//               subTitle={
//                 hasUserAccount
//                   ? 'Clique no botão abaixo para fazer login na plataforma ou efetuar o cadastro caso não tenha usuário.'
//                   : 'Seus dados foram salvos. Clique abaixo para cadastrar sua senha e ativar o acesso.'
//               }
//               extra={[
//                 <Button
//                   type="primary"
//                   size="large"
//                   key="proceed"
//                   block
//                   style={{
//                     backgroundColor: '#0038a8',
//                     borderColor: '#0038a8',
//                     height: 46,
//                     fontWeight: 600,
//                     borderRadius: 4
//                   }}
//                   onClick={handleProceed}>
//                   {hasUserAccount
//                     ? 'Ir para o Login'
//                     : 'Cadastrar Senha de Acesso'}
//                 </Button>
//               ]}
//             />
//           )}
//         </Card>
//       </div>
//     </div>
//   );
// };

// export const VerificationPage =
//   Form.create<FormComponentProps>()(VerificationPageBase);
